import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Bot, Check, Copy, Loader2 } from 'lucide-react';
import { formatDateBR, toDateSP } from '@lira/shared';
import type { ApiToken, ApiTokensResponse, CreateApiTokenResponse } from '@lira/shared';
import { apiDelete, apiGet, apiPost } from '@/lib/api';
import { errorMessage } from '@/lib/errorMessage';
import { toast } from '@/stores/toast';
import { ConfirmDialog, Modal, Skeleton } from '@/components/ui';
import { Card } from './SettingsPage';

/** ISO datetime -> 'DD/MM/AAAA' (em America/Sao_Paulo). */
const dateBR = (iso: string) => formatDateBR(toDateSP(iso));

const EXPIRATION_OPTIONS = [
  { label: 'Nunca expira', value: '' },
  { label: '30 dias', value: '30' },
  { label: '90 dias', value: '90' },
  { label: '1 ano', value: '365' },
] as const;

/**
 * Config → Segurança: tokens de API para agentes de IA (MCP) e integrações.
 * O token completo aparece UMA única vez, num modal com botão de copiar.
 */
export function ApiTokensCard() {
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [expiresInDays, setExpiresInDays] = useState('');
  const [created, setCreated] = useState<CreateApiTokenResponse | null>(null);
  const [confirmTarget, setConfirmTarget] = useState<ApiToken | null>(null);

  const tokensQuery = useQuery({
    queryKey: ['apiTokens'],
    queryFn: () => apiGet<ApiTokensResponse>('/tokens'),
  });

  const create = useMutation({
    mutationFn: () =>
      apiPost<CreateApiTokenResponse>('/tokens', {
        name: name.trim(),
        expiresInDays: expiresInDays ? Number(expiresInDays) : undefined,
      }),
    onSuccess: (res) => {
      setCreated(res);
      setName('');
      setExpiresInDays('');
      void queryClient.invalidateQueries({ queryKey: ['apiTokens'] });
    },
    onError: (err) => toast(errorMessage(err), 'error'),
  });

  const revoke = useMutation({
    mutationFn: (id: string) => apiDelete(`/tokens/${id}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['apiTokens'] });
      toast('Token revogado.', 'success');
      setConfirmTarget(null);
    },
    onError: (err) => toast(errorMessage(err), 'error'),
  });

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (create.isPending || !name.trim()) return;
    create.mutate();
  };

  const tokens = tokensQuery.data?.tokens;

  return (
    <Card>
      <div className="flex items-center gap-2">
        <span className="grid size-8 place-items-center rounded-full bg-primary-weak text-primary">
          <Bot className="size-4" />
        </span>
        <h2 className="text-lg font-semibold text-ink">Tokens de API</h2>
      </div>
      <p className="mt-1 text-sm text-muted">
        Credenciais para agentes de IA (MCP) e integrações acessarem sua conta: leitura e
        lançamentos, sem excluir dados nem mudar configurações. Revogue quando não usar mais.
      </p>

      <form onSubmit={handleSubmit} className="mt-4 flex max-w-xl flex-wrap items-end gap-3">
        <label className="min-w-0 flex-1 basis-52">
          <span className="mb-1 block text-[13px] font-medium text-muted">Nome do token</span>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder='Ex.: "Assistente Claude"'
            maxLength={60}
            className="h-11 w-full rounded-lg border border-field bg-surface px-3 text-[15px] text-ink transition-colors focus:border-primary focus:outline-none"
          />
        </label>
        <label className="basis-36">
          <span className="mb-1 block text-[13px] font-medium text-muted">Expiração</span>
          <select
            value={expiresInDays}
            onChange={(e) => setExpiresInDays(e.target.value)}
            className="h-11 w-full rounded-lg border border-field bg-surface px-3 text-[15px] text-ink transition-colors focus:border-primary focus:outline-none"
          >
            {EXPIRATION_OPTIONS.map((opt) => (
              <option key={opt.label} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </label>
        <button
          type="submit"
          disabled={create.isPending || !name.trim()}
          className="flex h-11 items-center gap-2 rounded-full bg-primary px-5 text-sm font-semibold uppercase tracking-wide text-white transition-colors hover:bg-primary-hover disabled:opacity-60"
        >
          {create.isPending && <Loader2 className="size-4 animate-spin" />}
          Gerar token
        </button>
      </form>

      <div className="mt-4">
        {tokensQuery.isLoading ? (
          <div className="space-y-3" aria-hidden>
            {[0, 1].map((i) => (
              <Skeleton key={i} className="h-14 rounded-lg" />
            ))}
          </div>
        ) : tokensQuery.isError ? (
          <p className="py-6 text-center text-sm text-expense">{errorMessage(tokensQuery.error)}</p>
        ) : !tokens || tokens.length === 0 ? (
          <p className="py-8 text-center text-sm text-muted">
            Nenhum token ativo. Gere um para conectar um assistente à sua conta.
          </p>
        ) : (
          <ul className="divide-y divide-border">
            {tokens.map((token) => (
              <TokenRow
                key={token.id}
                token={token}
                onRevoke={setConfirmTarget}
                revoking={revoke.isPending && revoke.variables === token.id}
              />
            ))}
          </ul>
        )}
      </div>

      <ShowTokenModal created={created} onClose={() => setCreated(null)} />

      <ConfirmDialog
        open={confirmTarget !== null}
        title="Revogar token?"
        message={
          confirmTarget
            ? `"${confirmTarget.name}" (${confirmTarget.tokenPrefix}…) deixará de funcionar imediatamente. Agentes que o usam perderão o acesso.`
            : ''
        }
        confirmLabel="Revogar"
        loading={revoke.isPending}
        onConfirm={() => confirmTarget && revoke.mutate(confirmTarget.id)}
        onCancel={() => setConfirmTarget(null)}
      />
    </Card>
  );
}

function TokenRow({
  token,
  onRevoke,
  revoking,
}: {
  token: ApiToken;
  onRevoke: (token: ApiToken) => void;
  revoking: boolean;
}) {
  return (
    <li className="flex items-center gap-3 py-3">
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <p className="truncate font-medium text-ink">{token.name}</p>
          <code className="shrink-0 rounded bg-canvas px-1.5 py-0.5 font-mono text-[12px] text-muted">
            {token.tokenPrefix}…
          </code>
        </div>
        <p className="truncate text-[13px] text-muted">
          Criado em {dateBR(token.createdAt)}
          {' · '}
          {token.expiresAt ? `expira em ${dateBR(token.expiresAt)}` : 'nunca expira'}
          {' · '}
          {token.lastUsedAt ? `último uso ${dateBR(token.lastUsedAt)}` : 'nunca usado'}
        </p>
      </div>
      <button
        type="button"
        onClick={() => onRevoke(token)}
        disabled={revoking}
        className="shrink-0 rounded-full px-3 py-1.5 text-sm font-semibold text-muted transition-colors hover:bg-expense-weak hover:text-expense disabled:opacity-50"
      >
        Revogar
      </button>
    </li>
  );
}

/** Modal de exibição única do token recém-criado, com copiar. */
function ShowTokenModal({
  created,
  onClose,
}: {
  created: CreateApiTokenResponse | null;
  onClose: () => void;
}) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    if (!created) return;
    try {
      await navigator.clipboard.writeText(created.token);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast('Não foi possível copiar. Selecione e copie manualmente.', 'error');
    }
  };

  return (
    <Modal open={created !== null} onClose={onClose} title="Token criado" maxWidth={520}>
      {created && (
        <div className="space-y-4">
          <p className="text-sm text-muted">
            Copie e guarde agora — por segurança, este token{' '}
            <strong className="text-ink">não será exibido novamente</strong>.
          </p>

          <div className="flex items-center gap-2">
            <code className="min-w-0 flex-1 break-all rounded-lg border border-border bg-canvas px-3 py-2.5 font-mono text-[13px] text-ink">
              {created.token}
            </code>
            <button
              type="button"
              onClick={() => void handleCopy()}
              aria-label="Copiar token"
              className="grid size-10 shrink-0 place-items-center rounded-full bg-primary text-white transition-colors hover:bg-primary-hover"
            >
              {copied ? <Check className="size-4" /> : <Copy className="size-4" />}
            </button>
          </div>

          <p className="text-[13px] text-muted">
            Use como <code className="font-mono">Authorization: Bearer {created.apiToken.tokenPrefix}…</code>{' '}
            no servidor MCP ou nas chamadas à API.
          </p>

          <button
            type="button"
            onClick={onClose}
            className="h-10 w-full rounded-full bg-canvas text-sm font-semibold text-ink transition-colors hover:bg-border"
          >
            Já copiei, fechar
          </button>
        </div>
      )}
    </Modal>
  );
}
